import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart as LineIcon, TrendingUp, Target } from "lucide-react";
import { AnnotationBanner } from "@/components/Annotation";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fM = (v) => v ? `${v.toFixed(0)}M` : "0";
const fPct = (v) => v ? `${v.toFixed(1)}%` : "—";

export default function Projections() {
  const [proj, setProj] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/financial/projections`),
      axios.get(`${API}/financial/pnl`),
      axios.get(`${API}/financial/balance-sheet`),
    ]).then(([pRes, plRes, bsRes]) => {
      setProj(pRes.data.data);
      setPnl(plRes.data);
      setBs(bsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !proj) return <div className="h-64 skeleton-shimmer rounded" />;

  const labels = proj.periods || [];

  // Revenue & margin chart
  const chartData = labels.map((l, i) => ({
    period: l,
    revenue: proj.net_revenue[i] || 0,
    grossProfit: proj.gross_profit[i] || 0,
    ebitda: proj.ebitda[i] || 0,
    pat: proj.pat[i] || 0,
  }));

  const marginData = labels.map((l, i) => ({
    period: l,
    gross: proj.gross_margin[i] || 0,
    ebitda: proj.ebitda_margin[i] || 0,
    pat: proj.pat_margin[i] || 0,
  }));

  const periods = pnl?.periods || [];
  const plData = pnl?.data || {};
  const bsData = bs?.data || {};

  return (
    <div className="space-y-5" data-testid="projections-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Projections</h1>
          <p className="text-sm text-slate-400 mt-1">5-year financial outlook &middot; FY2024A to FY2030E</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-emerald-400 border-emerald-800 bg-emerald-950/30 text-xs font-mono">
            3Y CAGR: {proj.revenue_cagr_3y}%
          </Badge>
          <Badge variant="outline" className="text-blue-400 border-blue-800 bg-blue-950/30 text-xs font-mono">
            5Y CAGR: {proj.revenue_cagr_5y}%
          </Badge>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "FY25 Revenue", value: fM(proj.net_revenue[1]), color: "text-blue-400" },
          { label: "FY30E Revenue", value: fM(proj.net_revenue[6]), color: "text-emerald-400" },
          { label: "FY30E EBITDA", value: fM(proj.ebitda[6]), color: "text-amber-400" },
          { label: "FY30E PAT", value: fM(proj.pat[6]), color: "text-violet-400" },
        ].map((k) => (
          <div key={k.label} className="glass-card rounded-md p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{k.label}</p>
            <p className={`font-space text-xl font-bold tracking-tight mt-1 ${k.color}`}>INR {k.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue Growth Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="revenue-projection-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />Revenue & Profitability Trajectory
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="projRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="projEbGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                    <p className="text-slate-300 font-medium mb-1">{label}</p>
                    {payload.map((p, i) => (
                      <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: INR {p.value?.toFixed(0)}M</p>
                    ))}
                  </div>
                );
              }} />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="url(#projRevGrad)" strokeWidth={2.5} name="Net Revenue" />
              <Area type="monotone" dataKey="ebitda" stroke="#f59e0b" fill="url(#projEbGrad)" strokeWidth={2} name="EBITDA" />
              <Area type="monotone" dataKey="pat" stroke="#8b5cf6" fill="none" strokeWidth={1.5} strokeDasharray="4 4" name="PAT" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margin Expansion Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="margin-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-400" />Margin Expansion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={marginData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                    <p className="text-slate-300 font-medium mb-1">{label}</p>
                    {payload.map((p, i) => (
                      <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: {p.value?.toFixed(1)}%</p>
                    ))}
                  </div>
                );
              }} />
              <Line type="monotone" dataKey="gross" stroke="#3b82f6" strokeWidth={2} name="Gross Margin" dot={{ r: 3, fill: '#3b82f6' }} />
              <Line type="monotone" dataKey="ebitda" stroke="#f59e0b" strokeWidth={2} name="EBITDA Margin" dot={{ r: 3, fill: '#f59e0b' }} />
              <Line type="monotone" dataKey="pat" stroke="#8b5cf6" strokeWidth={2} name="PAT Margin" dot={{ r: 3, fill: '#8b5cf6' }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <AnnotationBanner title="Projection Thesis">
        <p>Revenue CAGR of {proj.revenue_cagr_5y}% over 5 years is driven by: (1) Scaling the high-margin D2C website channel, (2) Offline store expansion, (3) New product launches starting FY28, and (4) Export market entry. EBITDA margin expands from {fPct(proj.ebitda_margin[1])} (FY25) to {fPct(proj.ebitda_margin[6])} (FY30E) as fixed-cost leverage kicks in at scale. The path to profitability is already demonstrated — PAT was positive in FY24 and FY25.</p>
      </AnnotationBanner>

      {/* Balance Sheet Summary */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="balance-sheet-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200">Balance Sheet Summary (INR Millions)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 uppercase w-44">Particulars</th>
                {(pnl?.labels || []).map((l) => <th key={l} className="text-right py-2 px-3 text-[10px] text-slate-500 uppercase">{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Fixed Assets", key: "fixed_assets", bold: true },
                { label: "Inventories", key: "inventories" },
                { label: "Trade Receivables", key: "trade_receivables" },
                { label: "Total Assets", key: "total_assets", bold: true, highlight: true },
                { label: "Share Capital", key: "share_capital" },
                { label: "Reserves & Surplus", key: "reserves" },
                { label: "Borrowings", key: "borrowings" },
                { label: "Trade Payables", key: "trade_payables" },
                { label: "Total Liabilities", key: "total_liabilities", bold: true, highlight: true },
              ].map((row) => (
                <tr key={row.key} className={`border-b border-slate-800/20 ${row.highlight ? "bg-slate-800/20" : ""}`}>
                  <td className={`py-1.5 px-3 text-xs ${row.bold ? "font-semibold text-slate-200" : "text-slate-400"}`}>{row.label}</td>
                  {periods.map((p) => {
                    const v = bsData[row.key]?.[p];
                    return <td key={p} className={`py-1.5 px-3 text-right font-mono text-[11px] ${row.bold ? "font-semibold text-slate-200" : "text-slate-400"}`}>{v ? v.toFixed(1) : "—"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
